import {
  objectResolver,
  resolveNumber,
  resolveString,
} from 'airship/utils/resolvers'
import {Resolver} from 'airship/utils/resolvers/types'

type Timestamped<T> = {
  value: T
  timestampMs: number
}
const timestampedStringResolver: Resolver<Timestamped<string>> = objectResolver(
  {
    value: resolveString,
    timestampMs: resolveNumber,
  }
)

export interface WebSdkStorage {
  setChannelId(channelId: string): Promise<void>
  setNamedUserId(namedUserId: string): Promise<void>
  getChannelId(): Promise<string | void>
  getNamedUserId(): Promise<string | void>
}

class LocalWebSdkStorage implements WebSdkStorage {
  private _storage

  // this prefix is to avoid collisions and so we can change it later if
  // necessary, the leading short-sha is random and means nothing.
  private _prefix = '030c89a:airship:web-sdk'
  private _channelKey = `${this._prefix}:channel`
  private _userKey = `${this._prefix}:user`

  constructor(storage: Storage) {
    this._storage = storage
  }

  private _getTimestampedString(key: string): Timestamped<string> | undefined {
    const raw = this._storage.getItem(key)
    if (!raw) {
      return
    }

    try {
      return timestampedStringResolver(JSON.parse(raw))
    } catch (e) {
      this._storage.removeItem(key)
    }
  }

  async setChannelId(channelId: string): Promise<void> {
    if (!channelId) throw new Error('invalid channel id')
    const data: Timestamped<string> = {
      value: channelId,
      timestampMs: Date.now(),
    }
    this._storage.setItem(this._channelKey, JSON.stringify(data))
  }
  async getChannelId(): Promise<string | void> {
    const data = await this._getTimestampedString(this._channelKey)
    return data?.value
  }

  async setNamedUserId(namedUserId: string): Promise<void> {
    if (!namedUserId) throw new Error('invalid named user id')
    const data: Timestamped<string> = {
      value: namedUserId,
      timestampMs: Date.now(),
    }
    this._storage.setItem(this._userKey, JSON.stringify(data))
  }
  async getNamedUserId(): Promise<string | void> {
    const data = await this._getTimestampedString(this._userKey)
    return data?.value
  }
}

export default function createDefaultWebSdkStorage(): WebSdkStorage {
  return createWebSdkStorage(window.localStorage)
}

export function createWebSdkStorage(storage: Storage): WebSdkStorage {
  return new LocalWebSdkStorage(storage)
}
